// 007 / 005 slice (P-HOME-1; SA-BAN-6): the Home hero. Shows a school's live banners one at a time
// (1600 × 480, title as the image's accessible name) with Previous / Next and "1 of N"; with no banners,
// a plain hero with the school's name. Also the school admin's live preview.
import { useState } from "react";
import { Image, StyleSheet, View } from "react-native";

import { useChromeKind } from "@/shell/chrome/useChromeKind";
import { makeStyles } from "@/theme/makeStyles";
import { useTheme } from "@/theme/useTheme";
import { ChevronIcon } from "@/ui/icons/ChevronIcon";
import { PressableTarget } from "@/ui/PressableTarget";
import { Text } from "@/ui/Text";

/** Keeps the last two words together, so a headline never ends on one word alone. */
function noWidow(text: string): string {
  return text.replace(/ (\S+)$/, "\u00A0$1");
}

export type HeroBanner = { id: string; title: string; imageUrl: string };

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    hero: {
      borderRadius: t.radius.card,
      overflow: "hidden",
      backgroundColor: t.color.surface,
      borderWidth: t.borderWidth.card,
      borderColor: t.color.border,
    },
    // Same floor as the skeleton, so a banner replacing it never shifts the page on a phone.
    image: { width: "100%", aspectRatio: 1600 / 480, minHeight: 120 },
    // No live banner: a solid brand panel at the banner's own proportions (no jump when one loads).
    fallback: {
      borderRadius: t.radius.card,
      backgroundColor: t.color.primary,
      paddingHorizontal: t.space.x2l,
      paddingVertical: t.space.lg,
      aspectRatio: 1600 / 480,
      minHeight: 120,
      justifyContent: "flex-end",
      gap: t.space.xxs,
    },
    // Desktop: no banner is missing content, not a banner, so it stays a compact strip.
    fallbackWide: { aspectRatio: undefined, minHeight: 0, height: 140 },
    skeleton: {
      borderRadius: t.radius.card,
      backgroundColor: t.color.surfaceAlt,
      borderWidth: t.borderWidth.card,
      borderColor: t.color.border,
      aspectRatio: 1600 / 480,
      minHeight: 120,
    },
    fallbackText: { color: t.onPrimary },
    controls: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: t.space.xxs,
    },
    middle: { flexDirection: "row", alignItems: "center", gap: t.space.sm },
    dots: { flexDirection: "row", gap: t.space.xxs },
    dot: {
      width: 6,
      height: 6,
      borderRadius: t.radius.pill,
      backgroundColor: t.color.border,
    },
    dotOn: { width: 16, backgroundColor: t.color.primary },
  }),
);

export function HeroCarousel({
  banners,
  schoolName,
  fallbackText,
  loading = false,
  expectBanners = true,
  testID = "home-hero",
}: {
  banners: readonly HeroBanner[];
  schoolName: string;
  fallbackText: string;
  /** True until the first answer: a neutral placeholder, never the "no banners" panel. */
  loading?: boolean;
  /**
   * What the last load for this school found. `false` sizes the desktop placeholder as the
   * fallback strip, so a school with no banners never collapses from banner height.
   */
  expectBanners?: boolean;
  testID?: string;
}) {
  const styles = useStyles();
  const theme = useTheme();
  const wide = useChromeKind() === "desktop";
  const [index, setIndex] = useState(0);
  if (loading) {
    return (
      <View
        style={[styles.skeleton, wide && !expectBanners && styles.fallbackWide]}
        testID={`${testID}-loading`}
        aria-busy
        accessibilityLabel={schoolName}
      />
    );
  }
  if (banners.length === 0) {
    return (
      <View
        style={[styles.fallback, wide && styles.fallbackWide]}
        testID={`${testID}-fallback`}
      >
        <Text
          size={wide ? "lg" : "sm"}
          weight="semiBold"
          style={styles.fallbackText}
        >
          {schoolName}
        </Text>
        <Text
          variant="heading"
          size={wide ? "x4l" : "x2l"}
          style={styles.fallbackText}
        >
          {noWidow(fallbackText)}
        </Text>
      </View>
    );
  }
  const at = Math.min(index, banners.length - 1);
  const banner = banners[at]!;
  const go = (step: number) =>
    setIndex((at + step + banners.length) % banners.length);
  return (
    <View
      style={styles.hero}
      testID={testID}
      role="region"
      aria-roledescription="carousel"
      aria-label={schoolName}
    >
      <Image
        source={{ uri: banner.imageUrl }}
        style={styles.image}
        accessibilityLabel={banner.title}
        accessible
        resizeMode="cover"
        testID={`${testID}-image`}
      />
      {banners.length > 1 ? (
        <View style={styles.controls}>
          <PressableTarget
            accessibilityLabel="Previous banner"
            onPress={() => go(-1)}
          >
            <ChevronIcon direction="left" color={theme.color.textMuted} />
          </PressableTarget>
          <View style={styles.middle}>
            <View style={styles.dots} aria-hidden>
              {banners.map((b, i) => (
                <View
                  key={b.id}
                  style={[styles.dot, i === at && styles.dotOn]}
                />
              ))}
            </View>
            <Text size="sm" tone="muted" testID={`${testID}-position`}>
              {`${at + 1} of ${banners.length}`}
            </Text>
          </View>
          <PressableTarget
            accessibilityLabel="Next banner"
            onPress={() => go(1)}
          >
            <ChevronIcon color={theme.color.textMuted} />
          </PressableTarget>
        </View>
      ) : null}
    </View>
  );
}
